// hooks/usePromoManagement.ts
import { useState, useEffect, useCallback } from "react";
import { DeliveryType } from "../../../../common/types/enums";
import { CartItem, PromoInterface } from "../../../../common/types/types";
import { useCartValidation } from "./useCartValidation";

interface UsePromoManagementProps {
  cartItems: CartItem[];
  deliveryType: DeliveryType;
  pickUpStore?: number;
  onGiftAutoSelect?: (giftItemId: number) => void; // 👈 добавил
}

export const usePromoManagement = ({
  cartItems,
  deliveryType,
  pickUpStore,
  onGiftAutoSelect,
}: UsePromoManagementProps) => {
  const [selectedPromo, setSelectedPromo] = useState<PromoInterface | null>(
    null,
  );
  const [discountCode, setDiscountCode] = useState<string>("");
  const [applicablePromos, setApplicablePromos] = useState<PromoInterface[]>(
    [],
  );

  const { findApplicablePromos, isPromoApplicable } = useCartValidation();

  // Обновляем список подходящих промо
  useEffect(() => {
    const storeId =
      deliveryType === DeliveryType.PICKUP ? pickUpStore : undefined;
    const promos = findApplicablePromos(cartItems, storeId, deliveryType);
    setApplicablePromos(promos);

    // Если выбранная промо больше не подходит - сбрасываем её
    if (selectedPromo && !promos.some((p) => p.id === selectedPromo.id)) {
      setSelectedPromo(null);
      setDiscountCode("");
    }
  }, [cartItems, deliveryType, pickUpStore, findApplicablePromos]);

  const validatePromo = useCallback((): boolean => {
    if (!selectedPromo) return true;

    const storeId =
      deliveryType === DeliveryType.PICKUP ? pickUpStore : undefined;
    return isPromoApplicable(
      selectedPromo,
      cartItems,
      storeId,
      discountCode,
      deliveryType,
    );
  }, [
    selectedPromo,
    cartItems,
    deliveryType,
    pickUpStore,
    discountCode,
    isPromoApplicable,
  ]);

  const handleSelectPromo = useCallback(
    (promo: PromoInterface | null) => {
      setSelectedPromo(promo);
      setDiscountCode("");

      // Если акция с подарком и только один товар-подарок — авто-выбираем
      if (promo?.giftBySum && promo.giftItemIds?.length === 1) {
        onGiftAutoSelect?.(promo.giftItemIds[0]);
      }
    },
    [onGiftAutoSelect],
  );

  return {
    selectedPromo,
    setSelectedPromo: handleSelectPromo,
    discountCode,
    setDiscountCode,
    applicablePromos,
    validatePromo,
  };
};